import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, deleteUser } from 'firebase/auth';
import { doc, writeBatch, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [profileLoading, setProfileLoading] = useState(false);

    useEffect(() => {
        let mounted = true;

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user && mounted) {
                    setUser(user);
                    setProfileLoading(true);

                    const { data: profile, error } = await authService.getUserProfile(user.uid);

                    if (mounted) {
                        if (profile) {
                            setUserProfile(profile);
                        } else if (error) {
                            await authService.createBasicProfile(user);
                            const { data: retryProfile } = await authService.getUserProfile(user.uid);
                            setUserProfile(retryProfile);
                        }
                        setProfileLoading(false);
                    }
                } else if (mounted) {
                    setUser(null);
                    setUserProfile(null);
                    setProfileLoading(false);
                }
            } catch (error) {
                if (mounted) {
                    setProfileLoading(false);
                }
            } finally {
                if (mounted) {
                    setInitializing(false);
                }
            }
        });

        return () => {
            mounted = false;
            unsubscribe();
        };
    }, []);

    const login = async (email, password) => {
        setLoading(true);
        try {
            const result = await authService.login(email, password);
            return result;
        } finally {
            setLoading(false);
        }
    };

    const register = async (email, password, userData) => {
        setLoading(true);
        try {
            const result = await authService.register(email, password, userData);
            return result;
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        setLoading(true);
        try {
            const result = await authService.signInWithGoogle();
            return result;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        setLoading(true);
        try {
            await authService.logout();
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (email) => {
        return await authService.resetPassword(email);
    };

    const updateProfile = async (userData) => {
        if (!user) return { error: 'No user logged in' };

        setProfileLoading(true);
        try {
            const result = await authService.updateUserProfile(user.uid, userData);
            if (!result.error) {
                setUserProfile({ ...userProfile, ...userData });
                toast.success('Profile updated successfully!');
            }
            return result;
        } finally {
            setProfileLoading(false);
        }
    };

    const refreshProfile = async () => {
        if (!user) return;

        setProfileLoading(true);
        try {
            const { data: profile } = await authService.getUserProfile(user.uid);
            setUserProfile(profile);
        } finally {
            setProfileLoading(false);
        }
    };

    const deleteUserAccount = async () => {
        if (!user) {
            return { error: 'No user logged in' };
        }

        try {
            const batch = writeBatch(db);

            const userProfileRef = doc(db, 'users', user.uid);
            batch.delete(userProfileRef);

            const userCartRef = doc(db, 'cart', user.uid);
            batch.delete(userCartRef);

            const ordersQuery = query(collection(db, 'orders'), where('userId', '==', user.uid));
            const ordersSnapshot = await getDocs(ordersQuery);

            ordersSnapshot.forEach((orderDoc) => {
                const orderRef = doc(db, 'orders', orderDoc.id);
                batch.update(orderRef, {
                    userId: 'deleted-user',
                    customerInfo: {
                        email: 'deleted-user@deleted.com',
                        name: 'Deleted User'
                    },
                    shippingInfo: {
                        ...orderDoc.data().shippingInfo,
                        firstName: 'Deleted',
                        lastName: 'User',
                        email: 'deleted-user@deleted.com',
                        phone: 'deleted',
                        address: '[Address Deleted]'
                    }
                });
            });

            await batch.commit();

            await deleteUser(user);

            setUser(null);
            setUserProfile(null);

            return { error: null };
        } catch (error) {
            return { error: error.message };
        }
    };

    const isAuthenticated = () => {
        return !!user;
    };

    const isProfileComplete = () => {
        if (!userProfile) return false;
        return !!(userProfile.firstName && userProfile.lastName);
    };

    const isReady = () => {
        return !initializing && !profileLoading;
    };

    const value = {
        user,
        userProfile,
        loading,
        initializing,
        profileLoading,
        login,
        register,
        signInWithGoogle,
        logout,
        resetPassword,
        updateProfile,
        refreshProfile,
        deleteUserAccount,
        isAuthenticated,
        isProfileComplete,
        isReady
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};